<script setup>
import InputLabel from "@/Components/InputLabel.vue";
import InputError from "@/Components/InputError.vue";
import NumberInput from "@/Components/NumberInput.vue";
import TextInput from "@/Components/TextInput.vue";
import SelectInput from "@/Components/SelectInput.vue";
import Checkbox from "@/Components/Checkbox.vue";
import Datepicker from "@vuepic/vue-datepicker";
import {useHelpers, useTenantTextRules} from "@/helpers";
import dayjs from "dayjs";
import {computed, ref, watch} from "vue";
import {useForm, usePage} from "@inertiajs/inertia-vue3";
import FrontendMediaUpload from "../../../Common/Media/FrontendMediaUpload.vue";
import { Inertia } from '@inertiajs/inertia';

const props = defineProps({
  project: Object,
  isClosedProject: {
    type: Boolean,
    default: false,
  },
  hideSave: {
    type: Boolean,
    default() {
      return false;
    }
  },
  elementTypes: Array,
  vendors: Array,
  element: null | Object,
  clickedSaveForm: Boolean,
});
const currentUser = usePage().props.value.auth.user;
const helpers = useHelpers();
const { resolveTextRule } = useTenantTextRules();
const minDate = dayjs().toDate();
const orderDate = ref(props.element?.order_date || null);

const mediaFiles = ref({
  I: (props.element && props.element.all_image_media) || [],
  F: (props.element && props.element.all_file_media) || []
});

const saving = ref(false);
const hasError = ref(false);
const delivered = ref(!!(props.element?.elem_delivered && props.element.elem_delivered === 'yes'));
const form = useForm({
  _method: 'PUT',
  element: {
    elementtype: props.element?.elementtype || null,
    vid: props.element?.vid || null,
    style: props.element?.style || null,
    color: props.element?.color || null,
    element_cost: props.element?.element_cost || null,
    element_labor: props.element?.element_labor || null,
    elem_conf_num: props.element?.elem_conf_num || null,
    elem_track_num: props.element?.elem_track_num || null,
    elem_delivered: props.element?.elem_delivered || null,
    order_date: props.element?.order_date || null,
    location: props.element?.location || null,
    yield: props.element?.yield || null,
    elem_quanity: props.element?.elem_quanity ? parseInt(props.element.elem_quanity) : null,
    image: null,
    file: null,
    image_media_id: props.element?.image_media_id || null,
    file_media_id: props.element?.file_media_id || null,
    extra_image_media: props.element?.extra_image_media || null,
    extra_file_media: props.element?.extra_file_media || null,
    images: [], 
    files: []  
  }
});

const selectImageRef = ref(null);
const selectedImage = ref(null);
const imagePreview = ref(null);
const selectFileRef = ref(null);
const selectedFile = ref(null);

const removeImage = () => {
  selectedImage.value = null;
  selectImageRef.value = null;
  imagePreview.value = null;
  form.element.image_media_id = null;
  form.element.image = null;
}

const removeFile = () => {
  selectedFile.value = null;
  selectFileRef.value = null;
  form.element.file_media_id = null;
  form.element.file = null;
}

const selectedElementType = ref(null);
if (props.element?.elementtype) {
  selectedElementType.value = _.find(props.elementTypes, type => type.element_id === props.element.elementtype);
}
watch(() => form.element.elementtype, (value) => {
  if (!value) {
    selectedElementType.value = null;
  }
  selectedElementType.value = _.find(props.elementTypes, type => type.element_id === value);
  form.element.location = null;
  form.element.yield = null;
}, {deep: true});

const emit = defineEmits(['saving']);

watch(() => saving.value, (value) => {
  emit('saving', {loading: value, hasError: hasError.value});
}, {deep: true});

watch(() => props.clickedSaveForm, value => {
  if (saving.value === true) {
    return;
  }
  if (value === true) {
    saveForm();
  }
})

const uploadedMedia = ref({ I: [], F: [] });

const mergeWithoutDuplicates = (existing, incoming) => {
  const fileNames = new Set(existing.map(file => file.file_name));
  const newFiles = incoming.filter(file => !fileNames.has(file.file_name));
  return [...existing, ...newFiles];
};

const handleMediaUpdate = (value) => {
  uploadedMedia.value = {
    I: mergeWithoutDuplicates(uploadedMedia.value.I, value.I),
    F: mergeWithoutDuplicates(uploadedMedia.value.F, value.F)
  };

  form.element.images = uploadedMedia.value.I.map(item => item.file);
  form.element.files = uploadedMedia.value.F.map(item => item.file);
};

const saveForm = () => {
  if (selectedImage.value) {
    form.element.image = selectedImage.value;
  }
  if (selectedFile.value) {
    form.element.file = selectedFile.value;
  }
  form.element.order_date = orderDate.value;
  form.element.elem_delivered = delivered.value === true ? 'yes' : 'no'
  saving.value = true;
  hasError.value = false;
  let saveRoute = route('admin.projects.update', {project: props.project.pid});
  if (props.element?.prj_element_id) {
    saveRoute = route('admin.projects.elements.update', {
      projectid: props.project.pid,
      elementid: props.element.prj_element_id
    });
  }

  form.element.images = uploadedMedia.value.I.filter(f => f.type === 'I');
  form.element.files = uploadedMedia.value.F.filter(f => f.type === 'F');

  form.post(saveRoute, {
    preserveScroll: true,
    onSuccess() {
      form.reset();
      removeImage();
      removeFile();
      orderDate.value = null;
      hasError.value = false;
      saving.value = false;
      emit('updated');
    },
    onError() {
      hasError.value = true;
      saving.value = false;
    },
  });
}

const handleMediaDelete = (mediaItem, onSuccess, onError) => {
  const mediaId = mediaItem?.prjimageId || mediaItem?.id;

  if (!mediaId) {
    console.warn("No media ID found.");
    return;
  }

  Inertia.post(
    route('admin.media.destroy', { mediaid: mediaId }),
    { _method: 'DELETE' },
    {
      preserveScroll: true,
      onSuccess: () => {
        uploadedMedia.value.I = uploadedMedia.value.I.filter((f) => f.prjimageId !== mediaId && f.id !== mediaId);
        uploadedMedia.value.F = uploadedMedia.value.F.filter((f) => f.prjimageId !== mediaId && f.id !== mediaId);

        form.element.all_image_media = uploadedMedia.value.I.map(item => item.file);
        form.element.all_file_media = uploadedMedia.value.F.map(item => item.file);

        if (onSuccess) onSuccess();
      },
      onError: () => {
        if (onError) onError();
      },
    }
  );
};
const elementTypeLabel = computed(() =>
  resolveTextRule('project.elements.form.element_type_label', 'Element Type', 'project.elements.form.element_type_label')
);
const styleSkuLabel = computed(() =>
  resolveTextRule('project.elements.form.style', 'Style/SKU', 'project.elements.form.style')
);
const costPerUnitLabel = computed(() =>
  resolveTextRule(
    'project.elements.form.cost',
    'Cost per Unit',
    'project.elements.form.cost'
  )
);
const sizeDimensionsStitchCountLabel = computed(() =>
  resolveTextRule(
    'project.elements.form.labor',
    'Size / Dimensions / Stitch Count',
    'project.elements.form.labor'
  )
);

</script>

<template>
  <div class="sm:grid grid-cols-6 gap-6">
    <!-- Element Type -->
    <div class="col-span-6 sm:col-span-4">
      <InputLabel for="element-type" :value="elementTypeLabel" :required="true"/>
      <SelectInput id="element-type" v-model="form.element.elementtype"
              :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
              class="mt-1 disabled:bg-gray-50">
        <option :value="null">Please Select</option>
        <option v-for="elementType in elementTypes" :value="elementType.element_id"
                v-text="elementType.elements"></option>
      </SelectInput>
      <InputError class="mt-2" :message="form.errors['element.elementtype']"/>
    </div>
    <!-- Location -->
    <div class="col-span-6 sm:col-span-4" v-if="selectedElementType?.fields?.location?.enabled === true">
      <InputLabel for="location" value="Location"/>
      <TextInput
        id="location"
        type="text"
        class="mt-1 block w-full"
        v-model="form.element.location"
        :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
      />
      <InputError class="mt-2" :message="form.errors['element.location']"/>
    </div>
    <!-- Yield -->
    <div class="col-span-6 sm:col-span-4" v-if="selectedElementType?.fields?.yield?.enabled === true">
      <InputLabel for="yield" value="Yield"/>
      <TextInput
        id="yield"
        type="text"
        class="mt-1 block w-full"
        v-model="form.element.yield"
        :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
      />
      <InputError class="mt-2" :message="form.errors['element.yield']"/>
    </div>
    <!-- Vendor -->
    <div class="col-span-6 sm:col-span-4">
      <InputLabel for="vendor" value="Vendor" :required="true"/>
      <SelectInput id="vendor" v-model="form.element.vid" :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
              class="mt-1 disabled:bg-gray-50">
        <option :value="null">Please Select</option>
        <option v-for="vendor in vendors" :value="vendor.vendorID"
                v-text="vendor.vendorName"></option>
      </SelectInput>
      <InputError class="mt-2" :message="form.errors['element.vid']"/>
    </div>
    <!-- Quantity -->
    <div class="col-span-6 sm:col-span-4">
      <InputLabel for="quantity" value="Quantity"/>
      <NumberInput
        id="quantity"
        step="1"
        class="mt-1 block w-full"
        v-model="form.element.elem_quanity"
        :disabled="(!helpers.userCanManageProject(currentUser))"
      />
      <InputError class="mt-2" :message="form.errors['element.elem_quanity']"/>
    </div>
    <!-- Style -->
    <div class="col-span-6 sm:col-span-4">
      <InputLabel for="style" :value="styleSkuLabel"/>
      <TextInput
        id="style"
        type="text"
        class="mt-1 block w-full"
        v-model="form.element.style"
        :disabled="(!helpers.userCanManageProject(currentUser))"
      />
      <InputError class="mt-2" :message="form.errors['element.style']"/>
    </div>
    <!-- Color -->
    <div class="col-span-6 sm:col-span-4">
      <InputLabel for="color" value="Color"/>
      <TextInput
        id="color"
        type="text"
        class="mt-1 block w-full"
        v-model="form.element.color"
        :disabled="(!helpers.userCanManageProject(currentUser))"
      />
      <InputError class="mt-2" :message="form.errors['element.color']"/>
    </div>
    <!-- Cost -->
    <div class="col-span-6 sm:col-span-4">
      <InputLabel for="cost" :value="costPerUnitLabel"/>
      <TextInput
        id="cost"
        type="number"
        class="mt-1 block w-full"
        v-model="form.element.element_cost"
        :disabled="(!helpers.userCanManageProject(currentUser))"
      />
      <InputError class="mt-2" :message="form.errors['element.color']"/>
    </div>
    <!-- Labor -->
    <div class="col-span-6 sm:col-span-4">
      <InputLabel for="labor" :value="sizeDimensionsStitchCountLabel"/>
      <TextInput
        id="labor"
        type="text"
        class="mt-1 block w-full"
        v-model="form.element.element_labor"
        :disabled="(!helpers.userCanManageProject(currentUser))"
      />
      <InputError class="mt-2" :message="form.errors['element.element_labor']"/>
    </div>
    <!-- Order Date -->
    <div class="col-span-6 sm:col-span-4">
      <InputLabel for="lap-dip" value="Order Date"/>
      <Datepicker class="mt-1 block w-full disabled:bg-gray-50" v-model="orderDate" format="MMMM d, yyyy"
                  :min-date="minDate"
                  :disabled="(!helpers.userCanManageProject(currentUser))"
                  model-type="MM/dd/yyyy" auto-apply :close-on-auto-apply="true" :enable-time-picker="false"/>
      <InputError class="mt-2" :message="form.errors['element.order_date']"/>
    </div>
    <!-- Confirmation # -->
    <div class="col-span-6 sm:col-span-4">
      <InputLabel for="confirmation-number" value="Confirmation Number"/>
      <TextInput
        id="confirmation-number"
        type="text"
        class="mt-1 block w-full"
        v-model="form.element.elem_conf_num"
        :disabled="(!helpers.userCanManageProject(currentUser))"
      />
      <InputError class="mt-2" :message="form.errors['element.elem_conf_num']"/>
    </div>
    <!-- Tracking # -->
    <div class="col-span-6 sm:col-span-4">
      <InputLabel for="tracking-number" value="Tracking Number"/>
      <TextInput
        id="tracking-number"
        type="text"
        class="mt-1 block w-full"
        v-model="form.element.elem_track_num"
        :disabled="(!helpers.userCanManageProject(currentUser))"
      />
      <InputError class="mt-2" :message="form.errors['element.elem_track_num']"/>
    </div>
    <!-- Delivered -->
    <div class="col-span-6 sm:col-span-4">
      <InputLabel for="delivered">
        <div class="flex items-center">
          Delivered
          <Checkbox id="delivered" class="ml-1"
                    :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
                    v-model:checked="delivered"/>
        </div>
      </InputLabel>
      <InputError class="mt-2" :message="form.errors['element.elem_track_num']"/>
    </div>
    
    <div class="col-span-6 sm:col-span-4 mt-4">
      <InputLabel value="Upload Media (Images or Files)"/>
      <FrontendMediaUpload
        v-if="!element?.prj_element_id"
        @update="handleMediaUpdate"
        :can-see-upload-form="true"
        :can-modify-media-details="true"
      />
      <FrontendMediaUpload
        v-else
        @update="handleMediaUpdate"
        @delete="handleMediaDelete"
        :can-see-upload-form="true"
        :can-modify-media-details="true"
        :initial-files="mediaFiles"
      />

    </div>

  </div>

</template>
